/**
 * Lightweight locale-aware string accessor.
 *
 * - Zero-dependency. Drop-in-replaceable with react-i18next later.
 * - Resolves active locale from localStorage → <html lang> → navigator.language → "en".
 * - Add a locale: create ./locales/<code>.ts and register in LOCALES.
 */
import { useSyncExternalStore } from "react";
import { en, type LocaleStrings, type BohoCollectionHandle } from "./locales/en";
import { es } from "./locales/es";
import { ar } from "./locales/ar";

const LOCALES: Record<string, LocaleStrings> = {
  en,
  es,
  ar,
};

export const LOCALE_LABELS: Record<string, string> = {
  en: "English",
  es: "Español",
  ar: "العربية",
};

export const SUPPORTED_LOCALES = Object.keys(LOCALES);

/** ISO 639-1 codes that render right-to-left. */
export const RTL_LOCALES = new Set<string>(["ar", "he", "fa", "ur", "ps", "sd", "yi", "dv", "ku"]);

export type Direction = "ltr" | "rtl";

export function getDirection(code: string): Direction {
  return RTL_LOCALES.has(code) ? "rtl" : "ltr";
}

const DEFAULT_LOCALE = "en";
const STORAGE_KEY = "locale";
const EVENT_NAME = "localechange";

function readStoredLocale(): string | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && LOCALES[v] ? v : null;
  } catch {
    return null;
  }
}

export function resolveActiveLocale(): string {
  const stored = readStoredLocale();
  if (stored) return stored;
  if (typeof document !== "undefined") {
    const htmlLang = document.documentElement.lang?.split("-")[0];
    if (htmlLang && LOCALES[htmlLang]) return htmlLang;
  }
  if (typeof navigator !== "undefined") {
    const navLang = navigator.language?.split("-")[0];
    if (navLang && LOCALES[navLang]) return navLang;
  }
  return DEFAULT_LOCALE;
}

function applyLocaleToDocument(code: string): void {
  if (typeof document === "undefined") return;
  const dir = getDirection(code);
  const html = document.documentElement;
  html.lang = code;
  html.dir = dir;
  html.setAttribute("data-direction", dir);
}

/** Persist and apply a locale. Triggers re-render in useLocale subscribers. */
export function setLocale(code: string): void {
  if (!LOCALES[code]) return;
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* ignore */
  }
  applyLocaleToDocument(code);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(EVENT_NAME));
  }
}

/** Apply persisted locale + direction to <html> at app boot. */
export function initLocale(): void {
  applyLocaleToDocument(resolveActiveLocale());
}

export function getLocale(): LocaleStrings {
  return LOCALES[resolveActiveLocale()] ?? LOCALES[DEFAULT_LOCALE];
}

function subscribe(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(EVENT_NAME, cb);
  window.addEventListener("storage", cb);
  return () => {
    window.removeEventListener(EVENT_NAME, cb);
    window.removeEventListener("storage", cb);
  };
}

/** Active locale code, re-renders on change. */
export function useActiveLocale(): string {
  return useSyncExternalStore(subscribe, resolveActiveLocale, () => DEFAULT_LOCALE);
}

/** Active text direction, re-renders on change. */
export function useDirection(): Direction {
  return getDirection(useActiveLocale());
}

/** Locale strings, re-renders on change. */
export function useLocale(): LocaleStrings {
  useActiveLocale();
  return getLocale();
}

export type { LocaleStrings, BohoCollectionHandle };
export { en };
